import { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { motion } from 'motion/react';
import BoostToggle from '@/shared/components/swap-flow/RouteList/BoostToggle';
import RouteCard, {
  RouteCardLoading,
  RouteCardNotFound,
} from '@/shared/components/swap-flow/RouteList/RouteCard';
import { SlippageTolerancePopup } from '@/shared/components/swap-flow/RouteList/SlippageTolerancePopup';
import { isTestnet } from '@/shared/featureFlags';
import useBoost from '@/shared/hooks/useBoost';
import { useCountdown } from '@/shared/hooks/useCountdown';
import useSettingsStore from '@/shared/hooks/useSettingsStore';
import useSwapRequestStore, { selectShowRouteList } from '@/shared/hooks/useSwapRequestStore';
import { useSwapRoutes } from '@/shared/hooks/useSwapRoutes';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import useTracking from '@/shared/hooks/useTracking';
import { type RouteResponse } from '@/shared/integrations';
import { SwapEvents, type SwapTrackEvents } from '@/shared/track/swap';

const MIN_ALLOWED_NEGATIVE_DELTA_PERCENT = -5;

export const RouteList = () => {
  const track = useTracking<SwapTrackEvents>();
  const showRouteList = useSwapRequestStore(selectShowRouteList);
  const srcToken = useSwapRequestStore((state) => state.srcToken);
  const destToken = useSwapRequestStore((state) => state.destToken);
  const srcAmount = useSwapRequestStore((state) => state.srcAmount);
  const isOnChain = useSettingsStore((state) => state.isOnChainSwap);
  const resetKey = [
    srcToken?.chain.id,
    destToken?.chain.id,
    srcToken?.address,
    destToken?.address,
    srcAmount,
    isOnChain,
  ].join('-');

  const {
    data: allRoutes = [],
    isPending: routesPending,
    refetch,
  } = useSwapRoutes({
    showRouteList,
    srcToken,
    destToken,
    srcAmount,
    isOnChain,
  });

  const { price: srcPrice } = useTokenPrice(srcToken);
  const { price: destPrice } = useTokenPrice(destToken);

  const routes = useMemo(() => {
    if (isTestnet) return allRoutes;
    if (!srcPrice || !destPrice) return allRoutes;
    return allRoutes.filter((route) => {
      const srcUsd = route.srcAmount.toUsdNumber(srcPrice);
      const destUsd = route.destAmount.toUsdNumber(destPrice);
      if (srcUsd === 0) return true;
      if (route.srcToken.chainflipId === 'Flip' || route.destToken.chainflipId === 'Flip') {
        return true; // Don't filter out routes if FLIP is involved
      }
      const deltaPct = ((destUsd - srcUsd) / srcUsd) * 100;
      return deltaPct >= MIN_ALLOWED_NEGATIVE_DELTA_PERCENT;
    });
  }, [allRoutes, srcPrice, destPrice]);

  const secondsUntilNextRefresh = useCountdown(30, !routesPending, refetch, resetKey);
  const selectedRoute = useSwapRequestStore((state) => state.selectedRoute);
  const setSelectedRoute = useSwapRequestStore((state) => state.setSelectedRoute);
  const isOnChainSwap = useSettingsStore((state) => state.isOnChainSwap);
  const { boostActive } = useBoost();

  useEffect(() => {
    setSelectedRoute(undefined);
  }, []);

  const memo = useMemo(() => {
    if (!routes.length) return undefined;

    const { best, worst, fastest } = routes.reduce(
      (acc, curr) => ({
        best: acc.best.destAmount.gt(curr.destAmount) ? acc.best : curr,
        worst: acc.worst.destAmount.lt(curr.destAmount) ? acc.worst : curr,
        fastest: acc.fastest.durationSeconds < curr.durationSeconds ? acc.fastest : curr,
      }),
      { best: routes[0], worst: routes[0], fastest: routes[0] },
    );

    const differenceFromWorstRoute = best.destAmount.sub(worst.destAmount).toCapped();

    const chainflipRoute = routes.find((route) => route.integration === 'chainflip');
    const recommendedRoute = chainflipRoute ?? best;

    return {
      bestRoute: best,
      fastestRoute: fastest,
      differenceFromWorstRoute,
      recommendedRoute,
    };
  }, [routes, selectedRoute]);

  useEffect(() => {
    const updatedSelectedRoute =
      selectedRoute && routes.find((route) => route.id === selectedRoute.id);

    setSelectedRoute(updatedSelectedRoute ?? memo?.recommendedRoute);
  }, [routes, memo, selectedRoute]);

  const onRouteSelected = useCallback(
    (route: RouteResponse) => {
      if (selectedRoute?.id !== route.id) {
        track(SwapEvents.SelectRoute, {
          props: {
            assetFrom: `${route.srcToken.chain.chainflipId}.${route.srcToken.symbol}`,
            assetFromAmount: route.srcAmount.toFixed(),
            assetTo: `${route.destToken.chain.chainflipId}.${route.destToken.symbol}`,
            quotedAmount: route.destAmount.toFixed(),
            estRate: route.destAmount.ratio(route.srcAmount).toFixed(),
            isRecommended: route.id === memo?.recommendedRoute?.id,
            isNative: route.integration === 'chainflip',
            integration: route.integration,
            isBoostable: boostActive,
            dcaParams:
              route.integration === 'chainflip' ? route.integrationData.dcaParams : undefined,
          },
        });
      }
      setSelectedRoute(route);
    },
    [selectedRoute],
  );

  return (
    <div className="flex w-full flex-col space-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-4 shadow-grayPop1 md:w-[430px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <span className="font-aeonikMedium text-14 text-cf-light-4">Route</span>
          <span className="font-aeonikMedium text-12 text-cf-light-2">
            ({Number(secondsUntilNextRefresh)}s)
          </span>
        </div>
        <div className="flex items-center gap-x-2">
          {!isOnChainSwap && <BoostToggle />}
          <SlippageTolerancePopup />
        </div>
      </div>

      {routesPending && routes.length === 0 && <RouteCardLoading />}
      {!routesPending && routes.length === 0 && <RouteCardNotFound />}

      <motion.div
        className={classNames('space-y-5 font-aeonikMedium text-12 text-cf-light-2')}
        animate="open"
        variants={{
          open: { height: 'auto', opacity: 1 },
          closed: { height: 0, opacity: 1 },
        }}
        transition={{ duration: 0.15 }}
        exit={{ opacity: 0, height: 0 }}
      >
        <div className="h-full w-full space-y-4">
          {routes.map((route) => (
            <RouteCard
              key={route.id}
              fastest={route.id === memo?.fastestRoute.id && routes.length > 1}
              bestValue={route.id === memo?.bestRoute.id}
              route={route}
              isSelected={selectedRoute?.id === route.id}
              onRouteSelected={onRouteSelected}
              differenceFromWorstRoute={
                memo?.bestRoute.id === route.id ? memo.differenceFromWorstRoute : undefined
              }
              secondsUntilNextRefresh={secondsUntilNextRefresh}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};
